import {
  BanIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
} from "@heroicons/react/outline";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrettyLanguageName } from "../../../languages/util/prettyLanguageName";
import { isGenerationProposal, isPropagationProposal, Turn } from "../../util/slopifyThread";
import { buildDiffGroups } from "./diff/diffGroups";
import { StructuredAfterPreview } from "./diff/StructuredDiff";
import { useScopeContext } from "./ScopeContext";

interface HistoryAccordionProps {
  turns: Turn[];
  activeLanguageId: number;
  onRestore: (turn: Turn) => void;
  onRetry?: (turn: Turn) => void;
  onPropagateAccepted?: (turn: Turn) => void;
  canPropagate?: boolean;
}

const StatusIcon = ({ status }: { status: Turn["status"] }) => {
  const className = "w-3.5 h-3.5 shrink-0";
  if (status === "accepted") return <CheckCircleIcon className={`${className} text-green-600`} />;
  if (status === "rejected") return <MinusCircleIcon className={`${className} text-gray-400`} />;
  if (status === "aborted") return <BanIcon className={`${className} text-gray-400`} />;
  if (status === "failed") return <ExclamationCircleIcon className={`${className} text-red-500`} />;
  return null;
};

const formatTimestamp = (ms: number): string => {
  try {
    const d = new Date(ms);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value.length > 0 ? value : "—";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const HistoryAccordion = ({
  turns,
  activeLanguageId,
  onRestore,
  onRetry,
  onPropagateAccepted,
  canPropagate = false,
}: HistoryAccordionProps) => {
  const { t } = useTranslation();
  const { modelName } = useScopeContext();
  const prettyLanguageName = usePrettyLanguageName();
  const [expanded, setExpanded] = useState(false);
  const [openTurnId, setOpenTurnId] = useState<string | null>(null);

  const ordered = useMemo(() => turns.slice().sort((a, b) => b.createdAt - a.createdAt), [turns]);

  if (ordered.length === 0) return null;

  const languageLabel = prettyLanguageName(activeLanguageId);

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <button
        type="button"
        className="w-full flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <span>
          {t("general.assistantScenarIa.history.accordionLabel", {
            count: ordered.length,
          })}
        </span>
      </button>
      {expanded && (
        <div className="mt-1 slopify-collapse-in">
          {ordered.map((turn) => {
            const isOpen = openTurnId === turn.id;
            const generation = isGenerationProposal(turn.proposal) ? turn.proposal : null;
            const propagation = isPropagationProposal(turn.proposal) ? turn.proposal : null;
            const canRestore = !!generation && generation.fields.length > 0;
            const canRetry = (turn.status === "failed" || turn.status === "aborted") && !!onRetry;
            const isAccepted = turn.status === "accepted";
            const acceptedFieldNames = turn.acceptedFieldNames ?? [];
            const showPropagateButton =
              isAccepted &&
              !!generation &&
              canPropagate &&
              acceptedFieldNames.length > 0 &&
              !!onPropagateAccepted;

            return (
              <div key={turn.id} className="border-b border-gray-50 last:border-b-0">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 text-[11px] text-gray-600 py-1 hover:bg-gray-50 rounded"
                  onClick={() => setOpenTurnId(isOpen ? null : turn.id)}
                  aria-expanded={isOpen}
                >
                  <StatusIcon status={turn.status} />
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {formatTimestamp(turn.createdAt)}
                  </span>
                  <span
                    className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-gray-100 text-gray-500 whitespace-nowrap"
                    title={languageLabel}
                  >
                    {languageLabel}
                  </span>
                  <span className="truncate flex-1 text-left" title={turn.prompt}>
                    {turn.prompt || t("general.assistantScenarIa.historyNoPrompt")}
                  </span>
                  {isAccepted && typeof turn.appliedCount === "number" && (
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {t("general.assistantScenarIa.historyAcceptedCount", {
                        count: turn.appliedCount,
                      })}
                    </span>
                  )}
                  {isOpen ? (
                    <ChevronDownIcon className="w-3 h-3 text-gray-400 shrink-0" />
                  ) : (
                    <ChevronRightIcon className="w-3 h-3 text-gray-400 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="pl-4 pr-1 py-2 bg-gray-50 rounded-b">
                    {generation && generation.fields.length > 0 && (
                      <div className="space-y-1">
                        {buildDiffGroups(modelName, generation.fields, t as any).map((group) =>
                          group.kind === "text" ? (
                            <div
                              key={group.id}
                              className="bg-white rounded border border-gray-200 p-1.5"
                            >
                              <div className="text-[10px] font-medium text-gray-700 mb-0.5 truncate">
                                {group.label}
                                {group.unchanged && (
                                  <span className="ml-1 italic text-gray-400">
                                    ({t("general.assistantScenarIa.unchanged")})
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-gray-800 whitespace-pre-wrap break-words max-h-20 overflow-hidden">
                                {formatValue(group.members[0].after)}
                              </div>
                            </div>
                          ) : (
                            <div
                              key={group.id}
                              className="bg-white rounded border border-gray-200 p-1.5"
                            >
                              <div className="text-[10px] font-medium text-gray-700 mb-0.5 truncate">
                                {group.label}
                              </div>
                              <div className="text-[10px] text-gray-800 max-h-40 overflow-hidden">
                                <StructuredAfterPreview group={group} />
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                    {propagation && (
                      <div className="text-[10px] text-gray-600">
                        {t("general.assistantScenarIa.history.propagationSummary", {
                          count: propagation.languages.length,
                        })}
                      </div>
                    )}
                    {turn.error && (turn.status === "failed" || turn.status === "aborted") && (
                      <div className="text-[10px] text-red-600">
                        {turn.error.message ||
                          t(`general.assistantScenarIa.errors.${turn.error.kind}`)}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      {canRestore && (
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded-md bg-green-500 text-white hover:bg-green-600"
                          onClick={() => onRestore(turn)}
                        >
                          {t("general.assistantScenarIa.history.restore")}
                        </button>
                      )}
                      {canRetry && onRetry && (
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                          onClick={() => onRetry(turn)}
                        >
                          {t("general.assistantScenarIa.retry")}
                        </button>
                      )}
                      {showPropagateButton && onPropagateAccepted && (
                        <button
                          type="button"
                          className="text-[11px] px-2 py-1 rounded-md border border-green-200 text-green-600 hover:bg-green-50 ml-auto"
                          onClick={() => onPropagateAccepted(turn)}
                        >
                          {t("general.assistantScenarIa.propagation.propagateButton")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoryAccordion;
